import { useState, useMemo, useEffect } from 'react';
import { LOTTERY_TYPE, TAB_VALUE } from '../constants';

export const useLotteryFilters = ({
  lotteries,
  initialTab = TAB_VALUE.POPULAR,
  selectedCountry,
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [hasAttemptedAutoSwitch, setHasAttemptedAutoSwitch] = useState(false);

  const countryFilteredLotteries = useMemo(() => {
    if (!selectedCountry) return lotteries;
    return lotteries.filter((l) => l.country === selectedCountry);
  }, [lotteries, selectedCountry]);

  const tabCounts = useMemo(
    () => ({
      [TAB_VALUE.POPULAR]: countryFilteredLotteries.some(
        (lottery) => lottery.is_popular === true
      ),
      [LOTTERY_TYPE.REGULAR]: countryFilteredLotteries.some(
        (lottery) => lottery.type === LOTTERY_TYPE.REGULAR
      ),
      [LOTTERY_TYPE.INSTANT]: countryFilteredLotteries.some(
        (lottery) => lottery.type === LOTTERY_TYPE.INSTANT
      ),
    }),
    [countryFilteredLotteries]
  );

  const filteredLotteries = useMemo(() => {
    if (activeTab === TAB_VALUE.POPULAR) {
      return countryFilteredLotteries.filter((lottery) => lottery.is_popular === true);
    }
    return countryFilteredLotteries.filter((lottery) => lottery.type === activeTab);
  }, [countryFilteredLotteries, activeTab]);

  useEffect(() => {
    if (countryFilteredLotteries.length === 0) {
      setHasAttemptedAutoSwitch(false);
      return;
    }

    if (filteredLotteries.length > 0 || hasAttemptedAutoSwitch) return;

    const fallbackOrder = [TAB_VALUE.POPULAR, LOTTERY_TYPE.REGULAR, LOTTERY_TYPE.INSTANT];

    const nextTab = fallbackOrder.find((tab) => {
      if (tab === activeTab) return false;
      return tabCounts[tab];
    });

    if (nextTab) setActiveTab(nextTab);
    setHasAttemptedAutoSwitch(true);
  }, [countryFilteredLotteries, filteredLotteries, activeTab, hasAttemptedAutoSwitch, tabCounts]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setHasAttemptedAutoSwitch(false);
  };

  return {
    activeTab,
    setActiveTab: handleTabChange,
    tabCounts,
    filteredLotteries,
  };
};
